import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ExternalLink,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Loader2,
  Copy,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-provider configuration. Each entry tells the dialog:
 *   - which brand colors to wear
 *   - where to send the user to grab keys
 *   - which fields to render and what to validate
 *
 * Adding a new provider (e.g. Razorpay) is a matter of dropping another
 * entry here and wiring the matching backend probe in
 * `/app/backend/routes/payment_providers_connect.py`.
 */
const PROVIDER_CONFIG = {
  stripe: {
    name: "Stripe",
    tagline: "Online payments, subscriptions, payouts",
    accent: "violet",
    dashboardUrl: "https://dashboard.stripe.com/apikeys",
    dashboardLabel: "Open my Stripe dashboard",
    docs: "https://stripe.com/docs/keys",
    fields: [
      {
        key: "publishable_key",
        label: "Publishable Key",
        placeholder: "pk_live_…",
        hint: "Starts with pk_live_ (production) or pk_test_ (test mode).",
        type: "text",
      },
      {
        key: "secret_key",
        label: "Secret Key",
        placeholder: "sk_live_…",
        hint: "Starts with sk_live_ or sk_test_. Treat like a password — never shared with customers.",
        type: "password",
        required: true,
      },
    ],
    instructions: [
      "Click the button above to open your Stripe dashboard (logs you straight to the API keys page).",
      "Copy your Publishable Key — it's safe to share, starts with pk_.",
      "Reveal and copy your Secret Key — keep this private, starts with sk_.",
      "Paste both into the fields below and click Test connection.",
    ],
  },
  square: {
    name: "Square",
    tagline: "In-person card reader & online checkout",
    accent: "emerald",
    dashboardUrl: "https://developer.squareup.com/apps",
    dashboardLabel: "Open my Square Developer Dashboard",
    docs: "https://developer.squareup.com/docs/build-basics/access-tokens",
    fields: [
      {
        key: "application_id",
        label: "Application ID",
        placeholder: "sq0idp-…",
        hint: "Production starts with sq0idp-, sandbox starts with sandbox-sq0idp-.",
        type: "text",
      },
      {
        key: "access_token",
        label: "Access Token",
        placeholder: "EAAAEs…",
        hint: "Live tokens start with EAAA. Sandbox tokens start with EAAAl.",
        type: "password",
        required: true,
      },
      {
        key: "location_id",
        label: "Location ID",
        placeholder: "L1A2B3C4…",
        hint: "Found under Locations in your Square dashboard. We can also auto-pick the first location after the test passes.",
        type: "text",
      },
    ],
    instructions: [
      "Open the Square Developer Dashboard with the button above.",
      "Select your application (or create one) — name it whatever you like.",
      "Switch to the Production tab unless you're testing.",
      "Copy the Application ID and the Access Token from the Credentials section.",
      "Grab a Location ID from the Locations tab and paste all three below.",
    ],
  },
  paypal: {
    name: "PayPal",
    tagline: "Buy now, online checkout, PayPal credit",
    accent: "sky",
    dashboardUrl: "https://developer.paypal.com/dashboard/applications/live",
    dashboardLabel: "Open my PayPal Developer Dashboard",
    docs: "https://developer.paypal.com/api/rest/authentication/",
    fields: [
      {
        key: "client_id",
        label: "Client ID",
        placeholder: "AV…",
        hint: "Found under My Apps & Credentials → REST API apps. Starts with AV.",
        type: "text",
        required: true,
      },
      {
        key: "client_secret",
        label: "Client Secret",
        placeholder: "EL…",
        hint: "On the same app page — click Show under Secret.",
        type: "password",
        required: true,
      },
    ],
    instructions: [
      "Sign in to the PayPal Developer Dashboard using the button above.",
      "Go to My Apps & Credentials → Live → REST API apps.",
      "Open your app (or create one) and copy the Client ID.",
      "Click Show next to Secret, copy it, paste both below.",
    ],
  },
};

const ACCENT_CLASSES = {
  violet: {
    icon: "bg-violet-100 text-violet-600",
    cta: "bg-violet-600 hover:bg-violet-700 text-white",
    badge: "border-violet-400 text-violet-600",
    ring: "ring-violet-500",
  },
  emerald: {
    icon: "bg-emerald-100 text-emerald-600",
    cta: "bg-emerald-600 hover:bg-emerald-700 text-white",
    badge: "border-emerald-400 text-emerald-600",
    ring: "ring-emerald-500",
  },
  sky: {
    icon: "bg-sky-100 text-sky-600",
    cta: "bg-sky-600 hover:bg-sky-700 text-white",
    badge: "border-sky-400 text-sky-600",
    ring: "ring-sky-500",
  },
};

/**
 * Guided Connect flow for Stripe / Square / PayPal.
 *
 * Hybrid behavior:
 * - On open we ask the backend if OAuth is configured for this provider
 *   (`GET /payment-providers/oauth/{provider}/url`). If yes, we offer a
 *   "Connect with 1-click OAuth" button.
 * - Until then we fall back to the guided manual-key wizard.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.provider              - "stripe" | "square" | "paypal"
 * @param {string} props.businessSlug
 * @param {object} props.initialValues         - { application_id, ... }
 * @param {(values, mode) => Promise<void>} props.onConnected
 *        Called after a successful test+save. Receives the validated
 *        creds dict and a `mode` string ("live"|"test"|"sandbox").
 * @param {() => void} props.onClose
 */
export default function ConnectProviderDialog({
  open,
  provider,
  businessSlug,
  initialValues,
  onConnected,
  onClose,
}) {
  const config = PROVIDER_CONFIG[provider];
  const accent = ACCENT_CLASSES[config?.accent] || ACCENT_CLASSES.violet;

  // Per-field state, seeded from initialValues whenever the dialog opens.
  const [values, setValues] = useState({});
  const [reveal, setReveal] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, mode} | {error}
  const [oauthUrl, setOauthUrl] = useState(null);
  const [checkingOauth, setCheckingOauth] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset state fresh every open so leftover values from a previous
    // provider don't bleed in.
    setValues({ ...(initialValues || {}) });
    setReveal({});
    setTestResult(null);
    setOauthUrl(null);

    if (!businessSlug || !provider) return;
    setCheckingOauth(true);
    axios
      .get(`${BASE_API}/b/${businessSlug}/payment-providers/oauth/${provider}/url`)
      .then((res) => setOauthUrl(res.data.url))
      .catch(() => setOauthUrl(null)) // 501 / not configured — manual path
      .finally(() => setCheckingOauth(false));
  }, [open, provider, businessSlug, initialValues]);

  if (!config) return null;

  const updateField = (k, v) => {
    setValues((prev) => ({ ...prev, [k]: v }));
    // Any edit invalidates a previous test result.
    setTestResult(null);
  };

  const missingRequired = config.fields
    .filter((f) => f.required)
    .some((f) => !values[f.key]);

  const buildBody = () => ({
    provider,
    [provider]: { ...values },
  });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await axios.post(
        `${BASE_API}/b/${businessSlug}/payment-providers/test`,
        buildBody()
      );
      setTestResult({ ok: true, ...resp.data });
      toast.success(`${config.name} connected — ${resp.data.mode || "live"} mode`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Test failed";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!testResult?.ok) {
      // Run a final test if user pressed Save without testing first.
      await handleTest();
      // We can't read state synchronously; bail and let the user click again.
      return;
    }
    setSaving(true);
    try {
      await onConnected({ ...values }, testResult.mode);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
        data-testid={`connect-${provider}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.icon}`}>
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span>Connect {config.name}</span>
                {testResult?.ok && (
                  <Badge variant="outline" className={`text-[10px] ${accent.badge}`} data-testid={`${provider}-test-passed-badge`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {testResult.mode || "live"} mode verified
                  </Badge>
                )}
              </div>
              <p className="text-xs font-normal text-slate-500 mt-0.5">{config.tagline}</p>
            </div>
          </DialogTitle>
          <DialogDescription className="pt-2">
            We'll verify your keys with {config.name} before saving — that way you'll know they work
            before your customers try to pay.
          </DialogDescription>
        </DialogHeader>

        {/* OAuth fast path — only shown if backend reports configured */}
        {oauthUrl && (
          <div className="rounded-lg border-2 border-dashed p-3 mb-1 bg-slate-50 dark:bg-slate-800/40" data-testid={`${provider}-oauth-block`}>
            <p className="text-xs uppercase tracking-wider font-semibold mb-2 text-slate-500">
              <Sparkles className="w-3 h-3 inline -mt-1 mr-1" /> 1-click connect
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
              Skip the copy-paste entirely — we'll redirect you to {config.name}, you authorize,
              and we save the token automatically.
            </p>
            <a href={oauthUrl} rel="noreferrer">
              <Button className={accent.cta} data-testid={`${provider}-oauth-btn`}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect with {config.name} OAuth
              </Button>
            </a>
            <p className="text-[11px] text-slate-400 mt-2">
              Prefer to paste keys instead? Use the manual form below — both work.
            </p>
          </div>
        )}

        {/* Step 1 — deep-link CTA */}
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
              Step 1 · Grab your keys
            </p>
            <a href={config.dashboardUrl} target="_blank" rel="noreferrer">
              <Button
                type="button"
                variant="outline"
                className={`w-full border-2 ring-2 ring-offset-2 ${accent.ring} ring-offset-white dark:ring-offset-slate-900`}
                data-testid={`${provider}-open-dashboard-btn`}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {config.dashboardLabel}
              </Button>
            </a>
            <ol className="mt-3 text-xs text-slate-600 dark:text-slate-400 space-y-1 pl-5 list-decimal">
              {config.instructions.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Step 2 — credential fields */}
          <div className="pt-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
              Step 2 · Paste them here
            </p>
            <div className="space-y-3">
              {config.fields.map((field) => {
                const isSecret = field.type === "password";
                const visible = !isSecret || reveal[field.key];
                return (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-sm flex items-center gap-2">
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={visible ? "text" : "password"}
                          value={values[field.key] || ""}
                          onChange={(e) => updateField(field.key, e.target.value.trim())}
                          placeholder={field.placeholder}
                          className="font-mono text-sm pr-9"
                          data-testid={`${provider}-${field.key}-input`}
                          autoComplete="off"
                          spellCheck={false}
                        />
                        {isSecret && (
                          <button
                            type="button"
                            onClick={() => setReveal((r) => ({ ...r, [field.key]: !r[field.key] }))}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                            aria-label={visible ? "Hide" : "Show"}
                          >
                            {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      {values[field.key] && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => copy(values[field.key])}
                          className="shrink-0"
                          title="Copy"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">{field.hint}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Test result strip */}
          {testResult && (
            <div
              className={`p-3 rounded-lg text-sm flex items-start gap-2 ${
                testResult.ok
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                  : "bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/30"
              }`}
              data-testid={`${provider}-test-result`}
            >
              {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <div>
                {testResult.ok ? (
                  <>
                    <p className="font-medium">Connection verified · {testResult.mode || "live"} mode</p>
                    <p className="text-xs mt-0.5 opacity-80">
                      Your keys authenticate against {config.name}. Click Save to make this live.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">Couldn't connect</p>
                    <p className="text-xs mt-0.5 opacity-80">{testResult.error}</p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid={`${provider}-connect-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleTest}
            disabled={missingRequired || testing}
            data-testid={`${provider}-connect-test-btn`}
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Test connection
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={missingRequired || !testResult?.ok || saving}
            className={accent.cta}
            data-testid={`${provider}-connect-save-btn`}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Save & connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
